import { MRT_ColumnDef } from 'mantine-react-table'
import { Text, Tooltip } from '@mantine/core'
import type { CSSProperties } from 'react'
import { useDispatch } from 'react-redux'
import { useState } from 'react'
import { applyCurrentAmountChange } from 'app/state/ducks/traded-facilities/thunks'
import { loadFMMetadataForSubTrade } from 'app/state/ducks/signable-document/thunks'
import { formatDateToShow } from 'app/utils/util-functions'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import EditValueModal from './edit-value-modal'
import { useTradeRefresh } from 'app/views/pages/trade-entry/detail-trade/use-trade-refresh'

const getTextStyle = (
  weight: number,
  lineHeight: string,
  color = '#413E3E',
  textAlign?: 'start' | 'end' | 'center',
): CSSProperties => ({
  fontFamily: 'Plus Jakarta Sans',
  fontSize: '14px',
  fontWeight: weight,
  lineHeight: lineHeight,
  textAlign: textAlign ?? 'center',
  color: color,
})

function TableHead({
  header,
  textAlign,
}: {
  header: string
  textAlign?: 'start' | 'end' | 'center'
}) {
  return (
    <div style={{ justifyContent: 'center', width: '100%' }}>
      <Text style={getTextStyle(700, '22px', '#413E3E', textAlign)}>
        {header}
      </Text>
    </div>
  )
}

export const columnDefsTransaction: MRT_ColumnDef<any>[] = [
  {
    header: 'Date',
    mantineTableHeadCellProps: {
      className: 'cell-width-100',
    },
    Header: () => <TableHead header={'Date'} textAlign="start" />,
    Cell: ({ row }) => {
      return (
        <div>
          <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'start' }}>
            {formatDateToShow(row.original.date)}
          </Text>
        </div>
      )
    },
  },
  {
    header: 'Type',
    mantineTableHeadCellProps: {
      className: 'cell-width-100',
    },
    Header: () => <TableHead header={'Type'} />,
    Cell: ({ row }) => {
      return (
        <div>
          <Text style={{ ...getTextStyle(600, '19px') }}>
            {row.original.type}
          </Text>
        </div>
      )
    },
  },
  {
    header: 'Original Value',
    mantineTableHeadCellProps: {
      className: 'cell-width-100',
    },
    Header: () => <TableHead header={'Original Value'} textAlign="end" />,
    Cell: ({ row }) => {
      return (
        <div>
          <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'end' }}>
            {row.original.originalValue}
          </Text>
        </div>
      )
    },
  },
  {
    header: 'Updated Value',
    mantineTableHeadCellProps: {
      className: 'cell-width-100',
    },
    Header: () => <TableHead header={'Updated Value'} textAlign="end" />,
    Cell: ({ row }) => {
      return (
        <div>
          <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'end' }}>
            {row.original.updatedValue}
          </Text>
        </div>
      )
    },
  },
  {
    header: 'Applied to Trade',
    mantineTableHeadCellProps: {
      className: 'cell-width-100',
    },
    Header: () => <TableHead header={'Applied to Trade'} />,
    Cell: ({ row }) => {
      const dispatch = useDispatch()
      const { refreshTrade } = useTradeRefresh()
      const [isProcessing, setIsProcessing] = useState(false)
      const [editModalOpen, setEditModalOpen] = useState(false)

      const linkStyle = (disabled: boolean) => ({
        color: disabled ? '#9CA3AF' : '#476DB9',
        textAlign: 'center',
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '18px',
        textDecorationLine: 'underline',
        textDecorationStyle: 'solid',
        textDecorationSkipInk: 'none',
        textDecorationThickness: '10%',
        textUnderlineOffset: 'auto',
        textUnderlinePosition: 'from-font',
        cursor: disabled ? 'not-allowed' : 'pointer',
      } as React.CSSProperties)

      // Already applied + sub-trade still open → show Edit Value link
      if (row.original.appliedToTrade === 'Yes' && row.original.isEditable) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Text
              style={linkStyle(isProcessing)}
              onClick={() => {
                if (isProcessing) return
                setEditModalOpen(true)
              }}
            >
              Edit Value
            </Text>
            {row.original.isValueOverridden && (
              <Tooltip label="User edited" withArrow styles={{ tooltip: { fontSize: '14px' } }}>
                <Text style={{ color: '#EE5959', fontWeight: 700, cursor: 'default', fontSize: '18px', lineHeight: '19px' }}>!</Text>
              </Tooltip>
            )}
            <EditValueModal
              opened={editModalOpen}
              close={() => setEditModalOpen(false)}
              row={row.original}
            />
          </div>
        )
      }

      // Applied (locked/signed) or explicitly No
      if (row.original.appliedToTrade === 'Yes' || row.original.appliedToTrade === 'No') {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Text
              style={getTextStyle(
                600,
                '19px',
                row.original.appliedToTrade === 'Yes' ? '#287A26' : '#EE5959',
              )}
            >
              {row.original.appliedToTrade}
            </Text>
            {row.original.isValueOverridden && (
              <Tooltip label="User edited" withArrow styles={{ tooltip: { fontSize: '14px' } }}>
                <Text style={{ color: '#EE5959', fontWeight: 700, cursor: 'default', fontSize: '18px', lineHeight: '19px' }}>!</Text>
              </Tooltip>
            )}
          </div>
        )
      }

      // Not applied yet + editable → Apply now only
      return (
        <div>
          <Text
            style={linkStyle(isProcessing)}
            onClick={async () => {
              if (isProcessing) return
              setIsProcessing(true)
              try {
                const body = {
                  masterTradeId: row.original.masterTradeId,
                  facilityId: row.original.facilityId,
                  currentFacilityAmountData: row.original.facilityAmountData,
                }
                const response = await applyCurrentAmountChange(body)
                if (response.success) {
                  // FM metadata is the source of truth for this row's
                  // appliedToTrade status; refresh it so the row flips to
                  // "Edit Value". Await the refetch so the button keeps
                  // showing "Applying..." until the row data is fresh —
                  // otherwise the cell briefly re-renders as "Apply now"
                  // between us clearing isProcessing and Redux updating.
                  // refreshTrade() re-reads this trade's records, traded
                  // facilities included (ADR_LOS-030).
                  const subTradeId = row.original.subTradeId?.id
                  if (subTradeId) {
                    await dispatch(loadFMMetadataForSubTrade(subTradeId) as any)
                  }
                  await refreshTrade()
                  setIsProcessing(false)
                } else {
                  setIsProcessing(false)
                  ErrorNotification({
                    title: 'Trade Transaction Update Failed',
                    message: response.payload || 'Transaction Failed',
                  })
                }
              } catch (error) {
                console.error('Error applying transaction:', error)
              }
            }}
          >
            {isProcessing ? 'Applying...' : 'Apply now'}
          </Text>
        </div>
      )
    },
  },
]
